import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { getProjectTdmAnalytics } from '../../../../../api/analytics';
import type { IGetProjectsServiceResData } from '../../../../../api/Sherpa/Project/modules/ProjectCore/types';
import placeholderProjectThumb from '../../../../../images/pictures/project-thumbnail-placeholder.png';
import { convertEnumToText, getInitials } from '../../../../../utils';
import { getTdmCompleteOverCount } from '../../../../../utils/analytics';
import ImageCircularAvatar from '../../../../UI/Avatar/ImageCircularAvatar';
import Badge from '../../../../UI/Badge';
import Styles from './ProjectCard.module.css';

function ProjectCard({ project }: { project: IGetProjectsServiceResData }) {
  //Uplift: useQuery v5
  const fetchProjectTdmAnalytics = useQuery({
    queryKey: [`project-tdm-analytic-${project.id}`],
    queryFn: () => getProjectTdmAnalytics(project.id),
  });
  const [isImageLoadError, setIsImageLoadError] = useState(false);

  const projectTdmAnalytics = fetchProjectTdmAnalytics?.data;
  const tasksStat = getTdmCompleteOverCount(projectTdmAnalytics?.tasks);
  const deliverablesStat = getTdmCompleteOverCount(projectTdmAnalytics?.deliverables);
  const milestonesStat = getTdmCompleteOverCount(projectTdmAnalytics?.milestones);

  return (
    <div className='w-auto shadow-lg rounded-md relative border-b-4 border-white hover:border-primaryColor-light transform hover:-translate-y-2 transition duration-300 ease-in-out cursor-pointer'>
      <div className='relative rounded-t-md hover:opacity-70 h-full'>
        {/* overlay */}
        <div className='absolute top-0 bg-black/20 h-full w-full rounded-t-md'></div>
        {/* project team lead */}
        <div className='flex text-white absolute bottom-0 m-4 items-center'>
          <ImageCircularAvatar imageUrl={project.projectLeader.profileImageUrl} fallbackText={getInitials(project.projectLeader.name)} />
          <div className='flex flex-col font-bold ml-4'>
            <span>{project.projectLeader.name}</span>
            <span>Project Team Lead</span>
          </div>
        </div>
        {/* project picture */}
        {project.thumbnailImageUrl && !isImageLoadError && (
          <img
            src={project.thumbnailImageUrl}
            alt='project-thumbnail'
            className='rounded-t-md object-cover h-64 w-full'
            onError={() => setIsImageLoadError(true)}
          />
        )}
        {(!project.thumbnailImageUrl || isImageLoadError) && (
          <img src={placeholderProjectThumb} alt='project-thumbnail' className='rounded-t-md object-cover h-64 w-full' />
        )}
      </div>
      {/* project brief */}
      <div className='p-3 text-neutralColor-dark bg-white rounded-b-md'>
        <div className='px-2 h-24'>
          <div className='flex justify-between items-center'>
            <h4 className='font-bold m-0 break-all'>{project.name ? project.name : 'unknown'}</h4>
            <span className='absolute top-4 right-4 space-x-2'>
              <Badge status={project.stage ? project.stage : 'neutral'} text={convertEnumToText(project.status)} />

              {project.archived && <Badge badgeType='tertiary' text='Archived' />}
            </span>
          </div>
          <p className='py-1 text-neutralColor-normal w-full truncate'>{project.fullAddress}</p>
        </div>

        <div className='right flex justify-between text-center text-sm'>
          <div className='flex flex-col'>
            <p className='mx-2 text-gray-900 '>Task</p>{' '}
            <div
              className={`font-semibold
            ${deliverablesStat.percentComplete < 100 && Styles.healthy}
            ${deliverablesStat.percentComplete < 50 && Styles.warning}
            ${deliverablesStat.percentComplete < 20 && Styles.danger}
            `}
            >
              {tasksStat.completeCount} / {tasksStat.totalCount}
            </div>
          </div>
          <div className='flex flex-col'>
            <p className='mx-2 text-gray-900'>Deliverables</p>{' '}
            <div
              className={`font-semibold
             ${deliverablesStat.percentComplete < 100 && Styles.healthy}
             ${deliverablesStat.percentComplete < 50 && Styles.warning}
             ${deliverablesStat.percentComplete < 20 && Styles.danger}
            `}
            >
              {deliverablesStat.completeCount} / {deliverablesStat.totalCount}
            </div>{' '}
          </div>
          <div className='flex flex-col'>
            <p className='mx-2 text-gray-900'>Milestones</p>{' '}
            <div
              className={`font-semibold
              ${milestonesStat.percentComplete < 100 && Styles.healthy}
              ${milestonesStat.percentComplete < 50 && Styles.warning}
              ${milestonesStat.percentComplete < 20 && Styles.danger}
              `}
            >
              {milestonesStat.completeCount} / {milestonesStat.totalCount}
            </div>{' '}
          </div>
        </div>
        {/* TO BE USED IN FUTURE VERSION */}
        {/* <div className="flex flex-col justify-between py-5">
          <p className="mb-2 text-gray-900 text-sm font-semibold">
            Project Health:
          </p>{" "}
          <div className="relative">
            <div className="absolute w-full h-3 bg-gray-300 rounded-full"></div>
            <div
              className={`absolute h-3 rounded-full ${
                parseInt(percentCompleted) > 50
                  ? "bg-green-500"
                  : "bg-yellow-500"
              }`}
              style={{ width: percentCompleted + "%" }}
            ></div>
          </div>
        </div> */}
      </div>
    </div>
  );
}

export default ProjectCard;
